import type React from "react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Modal from "~/components/ui/modal";
import { azureLoginUri } from "~/config/apiConfig";
import { TOAST_MESSAGES } from "~/constants/messages";
import { UserApi } from "~/functions/api/user";
import type { Organization } from "~/interface/organization";
import {
  clearAuth,
  clearIntendedUrl,
  setCurrentProject,
  setCurrentUser,
  setOrganization,
} from "~/slices/auth";
import type { RootState } from "~/store";
import styles from "./index.module.scss";

interface OrganizationSelectionModalProps {
  isOpen: boolean;
  organizations: Organization[];
  onClose: () => void;
  allowClose?: boolean;
}

export const OrganizationSelectionModal: React.FC<OrganizationSelectionModalProps> = ({
  isOpen,
  organizations,
  onClose,
  allowClose = true,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentUserInfo, intendedUrl } = useSelector((state: RootState) => state.auth);
  const [localOrganizations, setLocalOrganizations] = useState<Organization[]>([]);

  useEffect(() => {
    if (organizations && organizations.length > 0) {
      setLocalOrganizations(organizations);
    }
  }, [organizations]);

  useEffect(() => {
    if (isOpen) {
      if (organizations && organizations.length > 0) {
        setLocalOrganizations(organizations);
      }
    }
  }, [isOpen, organizations]);

  const handleGetCurrentUser = async (_user_id: string, organization_id: string) => {
    const userApi = new UserApi();
    const response = await userApi.get(organization_id);
    if (response) {
      const user = response;
      dispatch(setCurrentUser(user));
      // toast.success("ユーザー情報の取得に成功しました"); // 不要な成功トーストを削除
      return true;
    } else {
      toast.error(TOAST_MESSAGES.ORGANIZATION.FETCH_USER_FAILED);
      return false;
    }
  };

  const handleSelect = async (organization: Organization) => {
    try {
      const organizationId = currentUserInfo.user_data.find(
        (user) => user.organization_id === organization.id,
      )?.organization_id;

      const user_id = currentUserInfo.user_data.find(
        (user) => user.organization_id === organization.id,
      )?.user_id;

      if (organizationId && user_id) {
        await handleGetCurrentUser(user_id, organizationId);

        dispatch(setOrganization(organization));
        // 組織を切り替えた時は現在のプロジェクトをクリア
        dispatch(setCurrentProject(null));

        // モーダルを閉じる
        onClose();

        // URL復元処理を統一
        const storedIntendedUrl = sessionStorage.getItem("intendedUrl") || intendedUrl;
        const isProjectPage = storedIntendedUrl?.includes("/project/");

        if (
          isProjectPage ||
          !storedIntendedUrl ||
          storedIntendedUrl === "/signin" ||
          storedIntendedUrl === "/signup"
        ) {
          dispatch(clearIntendedUrl());
          sessionStorage.removeItem("intendedUrl");
          navigate(`/${organizationId}/home`);
        } else {
          const urlOrgId = storedIntendedUrl.split("/")[1];
          if (urlOrgId === organizationId) {
            dispatch(clearIntendedUrl());
            sessionStorage.removeItem("intendedUrl");
            // 少し遅延させてからナビゲーションを実行（Redux stateの更新を待つ）
            setTimeout(() => {
              navigate(storedIntendedUrl);
            }, 100);
          } else {
            // 異なる組織のURLの場合は、現在の組織のホームページに移動
            dispatch(clearIntendedUrl());
            sessionStorage.removeItem("intendedUrl");
            navigate(`/${organizationId}/home`);
          }
        }
      } else if (organizationId && !user_id) {
        dispatch(clearAuth());
        toast.error(TOAST_MESSAGES.ORGANIZATION.NOT_MEMBER);
        setTimeout(() => {
          const storedIntendedUrl = sessionStorage.getItem("intendedUrl") || intendedUrl;
          if (storedIntendedUrl && storedIntendedUrl !== "/signin") {
            console.error(
              "Preserving intended URL during organization membership error redirect:",
              storedIntendedUrl,
            );
          }
          dispatch(clearAuth());
          window.location.href = azureLoginUri;
        }, 3000);
      } else if (!organizationId && user_id) {
        toast.error(TOAST_MESSAGES.ORGANIZATION.NOT_EXIST);
        setTimeout(() => {
          const storedIntendedUrl = sessionStorage.getItem("intendedUrl") || intendedUrl;
          if (storedIntendedUrl && storedIntendedUrl !== "/signin") {
            console.error(
              "Preserving intended URL during organization existence error redirect:",
              storedIntendedUrl,
            );
          }
          dispatch(clearAuth());
          window.location.href = azureLoginUri;
        }, 3000);
      }
    } catch (_error) {
      toast.error(TOAST_MESSAGES.ORGANIZATION.SELECTION_ERROR);
      const storedIntendedUrl = sessionStorage.getItem("intendedUrl") || intendedUrl;
      if (storedIntendedUrl && storedIntendedUrl !== "/signin") {
        console.error(
          "Preserving intended URL during organization selection error redirect:",
          storedIntendedUrl,
        );
      }
      dispatch(clearAuth());
      window.location.href = azureLoginUri;
    }
  };

  const getOrgIcon = (planType: string) => {
    if (planType === "free") {
      return (
        <div className={`${styles["orgIcon"]} ${styles["freePlan"]}`}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 3v17a1 1 0 001 1h17v-2H5V3H3z" />
            <path d="M15 14v4h2v-4h-2zm-8 2v2h2v-2H7zm4-1v3h2v-3h-2z" />
          </svg>
        </div>
      );
    } else if (planType === "starter") {
      return (
        <div className={`${styles["orgIcon"]} ${styles["starterPlan"]}`}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 3v17a1 1 0 001 1h17v-2H5V3H3z" />
            <path d="M15 12v6h2v-6h-2zm-8 2v4h2v-4H7zm4-1v5h2v-5h-2z" />
          </svg>
        </div>
      );
    } else if (planType === "standard") {
      return (
        <div className={`${styles["orgIcon"]} ${styles["standardPlan"]}`}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 3v17a1 1 0 001 1h17v-2H5V3H3z" />
            <path d="M15 10v8h2v-8h-2zm-8 3v5h2v-5H7zm4-2v7h2v-7h-2z" />
          </svg>
        </div>
      );
    } else if (planType === "professional") {
      return (
        <div className={`${styles["orgIcon"]} ${styles["professionalPlan"]}`}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 3v17a1 1 0 001 1h17v-2H5V3H3z" />
            <path d="M15 8v10h2V8h-2zm-8 3v7h2v-7H7zm4-1v8h2v-8h-2z" />
          </svg>
        </div>
      );
    } else if (planType === "enterprise") {
      return (
        <div className={`${styles["orgIcon"]} ${styles["enterprisePlan"]}`}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 3v17a1 1 0 001 1h17v-2H5V3H3z" />
            <path d="M15 6v12h2V6h-2zm-8 2v10h2V8H7zm4-1v11h2V7h-2z" />
          </svg>
        </div>
      );
    } else {
      // デフォルトとして紫のアイコンを返す
      return (
        <div className={`${styles["orgIcon"]} ${styles["purple"]}`}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 3v17a1 1 0 001 1h17v-2H5V3H3z" />
            <path d="M15 10v8h2v-8h-2zm-8 4v4h2v-4H7zm4-2v6h2v-6h-2z" />
          </svg>
        </div>
      );
    }
  };

  const translatePlanType = (planType: string) => {
    switch (planType) {
      case "free":
        return "フリー";
      case "starter":
        return "スターター";
      case "standard":
        return "スタンダード";
      case "professional":
        return "プロフェッショナル";
      case "enterprise":
        return "エンタープライズ";
      default:
        return "不明なプラン";
    }
  };

  const renderOrganizationList = () => {
    const orgsToRender = localOrganizations.length > 0 ? localOrganizations : organizations;

    if (!orgsToRender || orgsToRender.length === 0) {
      return <div className={styles["noData"]}>表示できる組織がありません</div>;
    }

    return orgsToRender.map((org, index) => (
      <div key={org.id || index} className={styles["orgItem"]} onClick={() => handleSelect(org)}>
        {getOrgIcon(org.plan.type)}
        <div className={styles["orgInfo"]}>
          <div className={styles["orgName"]}>{org.name || "名称なし"}</div>
          <div className={styles["orgPlan"]}>{translatePlanType(org.plan.type)}</div>
        </div>
      </div>
    ));
  };

  const modalContent = (
    <div className={styles["modalContent"]}>
      <div className={styles["orgList"]}>{renderOrganizationList()}</div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      label="組織選択"
      content={modalContent}
      showCloseButton={allowClose}
      usePortal={true}
    />
  );
};
